import { DAILY_GUESS_ROWS } from '../../constants/games/dailyGame';
import {
  createEmptyDailyGuessBuckets,
  getDailyGuessBucketNumbers,
} from './dailyGuessBuckets';

const clampCount = (value) => {
  const parsed = Number(value);
  if (!Number.isFinite(parsed) || parsed < 0) return 0;
  return Math.floor(parsed);
};

const sanitizeLetterString = (value, maxLen = 5) =>
  String(value || '')
    .toUpperCase()
    .replace(/[^A-Z]/g, '')
    .slice(0, maxLen);

const clampRowIndex = (value) => {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return 0;
  return Math.max(0, Math.min(DAILY_GUESS_ROWS - 1, Math.floor(parsed)));
};

export const createEmptyDailyGuesses = () => Array(DAILY_GUESS_ROWS).fill('');

export const createEmptyDailyStats = () => ({
  guesses: createEmptyDailyGuessBuckets(),
  losses: 0,
  currentStreak: 0,
  maxStreak: 0,
});

export const normalizeDailyGuesses = (guesses) => {
  const normalized = createEmptyDailyGuesses();
  if (!Array.isArray(guesses)) return normalized;

  for (let i = 0; i < DAILY_GUESS_ROWS; i += 1) {
    normalized[i] = sanitizeLetterString(guesses[i], 5);
  }

  return normalized;
};

export const normalizeDailyAttempt = (rawAttempt) => {
  const guesses = normalizeDailyGuesses(rawAttempt?.guesses);
  const gameOver = Boolean(rawAttempt?.gameOver);
  const currentRow = clampRowIndex(rawAttempt?.currentRow);
  const currentGuess = gameOver ? '' : sanitizeLetterString(rawAttempt?.currentGuess, 5);
  const isWin = gameOver ? Boolean(rawAttempt?.isWin) : false;
  const guessesUsedRaw = Number(rawAttempt?.guessesUsed);
  const guessesUsed =
    gameOver && Number.isFinite(guessesUsedRaw)
      ? Math.max(1, Math.min(DAILY_GUESS_ROWS, Math.floor(guessesUsedRaw)))
      : null;

  return {
    guesses,
    currentGuess,
    currentRow,
    gameOver,
    isWin,
    guessesUsed,
  };
};

export const buildInProgressDailyAttempt = (gameState) => {
  const normalized = normalizeDailyAttempt({
    guesses: gameState?.guesses,
    currentGuess: gameState?.currentGuess,
    currentRow: gameState?.currentRow,
    gameOver: false,
    isWin: false,
    guessesUsed: null,
  });

  return {
    ...normalized,
    gameOver: false,
    isWin: false,
    guessesUsed: null,
  };
};

export const buildCompletedDailyAttempt = ({
  guesses,
  currentRow,
  isWin,
  guessesUsed,
}) =>
  normalizeDailyAttempt({
    guesses,
    currentGuess: '',
    currentRow,
    gameOver: true,
    isWin,
    guessesUsed,
  });

export const computeDailyStatsFromAttempts = (attemptRows) => {
  const stats = createEmptyDailyStats();
  if (!Array.isArray(attemptRows) || attemptRows.length === 0) {
    return stats;
  }

  const sorted = [...attemptRows].sort((a, b) =>
    String(a?.puzzle_date || a?.puzzleDate || '').localeCompare(
      String(b?.puzzle_date || b?.puzzleDate || ''),
    ),
  );

  for (const row of sorted) {
    const gameOver = Boolean(row?.game_over ?? row?.gameOver);
    if (!gameOver) continue;

    const isWin = Boolean(row?.is_win ?? row?.isWin);
    const guessesUsedRaw = Number(row?.guesses_used ?? row?.guessesUsed);
    const guessesUsed = Number.isFinite(guessesUsedRaw)
      ? Math.max(1, Math.min(DAILY_GUESS_ROWS, Math.floor(guessesUsedRaw)))
      : null;

    if (isWin && guessesUsed) {
      stats.guesses[guessesUsed] = (stats.guesses[guessesUsed] || 0) + 1;
      stats.currentStreak += 1;
      stats.maxStreak = Math.max(stats.maxStreak, stats.currentStreak);
      continue;
    }

    stats.losses += 1;
    stats.currentStreak = 0;
  }

  const sanitizedGuessBuckets = getDailyGuessBucketNumbers().reduce((buckets, guessNumber) => {
    buckets[guessNumber] = clampCount(stats.guesses[guessNumber]);
    return buckets;
  }, {});

  return {
    guesses: sanitizedGuessBuckets,
    losses: clampCount(stats.losses),
    currentStreak: clampCount(stats.currentStreak),
    maxStreak: clampCount(stats.maxStreak),
  };
};
